import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { IDocumentTable } from '@models/Forms/IForms';
import { ITableForm } from '@models/Forms/ITableForm';
import { IFieldElem } from '@models/IFormData';
import EditTable from '@molecules/formbuilder/controls/Table/controls/EditTable';
import { createTableFormBridge } from '@molecules/formbuilder/controls/Table/controls/tableFormBridge';
import { useActivated, useFormulaStateWithStatus, useFormulaDisplay } from '@molecules/formbuilder/hooks';
import { withRowParentFields } from '../rowFormDraft';
import './TableFormSubtables.scss';

interface ITableFormSubtablesProps {
    table: IDocumentTable;
    fields: Record<string, IFieldElem>;
    rowFields: Record<string, IFieldElem>;
    methods: UseFormReturn<ITableForm>;
    isEdit: boolean;
    docId?: string;
    setError: (errors?: string[]) => void;
}

const SubtableSection = ({
    table,
    fields,
    methods,
    isEdit,
    docId,
    setError,
    onChanged,
}: Omit<ITableFormSubtablesProps, 'rowFields'> & { onChanged: () => Promise<void> }) => {
    const { activated } = useActivated();
    const { value: visible } = useFormulaStateWithStatus(
        table.visibilityRules,
        fields,
        methods,
        isEdit,
        false,
        activated,
        { fallback: true },
    );
    const field = fields[table.key];
    const caption = useFormulaDisplay(table.captionFormula, fields, methods, isEdit, false, activated, {
        fallback: table.caption || table.name,
        evalDisplayFormat: table.evalCaptionFormula,
        evaluateOnEmpty: true,
    });
    const rows = useWatch({ control: methods.control, name: field.name as never }) as unknown;
    const count = Array.isArray(rows) ? rows.length : 0;
    if (!visible) return null;
    return (
        <details className="table-form-subtable" open>
            <summary className="table-form-subtable__heading">
                <span>{caption.value || table.caption || table.name || 'Вложенная таблица'}</span>
                <span className="table-form-subtable__count">{count}</span>
            </summary>
            <div className="table-form-subtable__body">
                <EditTable
                    table={table}
                    fields={fields}
                    formMethods={methods}
                    dataPath={field.name}
                    isEdit={isEdit}
                    isNew={false}
                    viewMode={!isEdit}
                    docId={docId}
                    setError={setError}
                    onSubChanged={onChanged}
                />
            </div>
        </details>
    );
};

const TableFormSubtables = ({
    table,
    fields,
    rowFields,
    methods,
    isEdit,
    docId,
    setError,
}: ITableFormSubtablesProps) => {
    const childFields = useMemo(() => withRowParentFields(fields, rowFields), [fields, rowFields]);
    const bridge = useMemo(
        () => createTableFormBridge({ fields, formMethods: methods, isEdit, isNew: false, setError }),
        [fields, methods, isEdit, setError],
    );
    const onChanged = async () => {
        const row = Object.fromEntries(
            Object.entries(rowFields).map(([key, field]) => [key, methods.getValues(field.name as never)]),
        );
        const calculated = await bridge.calculateRow(row, undefined, table);
        for (const column of table.tableColumnCalc ?? []) {
            const field = rowFields[column.key];
            if (field) methods.setValue(field.name as never, calculated[column.key] as never, { shouldDirty: true });
        }
    };
    return (
        <div className="table-form-subtables">
            {(table.tables ?? []).map((child) => (
                <SubtableSection
                    key={child.key}
                    table={child}
                    fields={childFields}
                    methods={methods}
                    isEdit={isEdit}
                    docId={docId}
                    setError={setError}
                    onChanged={onChanged}
                />
            ))}
        </div>
    );
};

export default TableFormSubtables;
